import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import Vue from 'vue';
import {RootState} from '../RootState';
import axios from "axios";
import {settings} from "@/plugins/settings";
import responseHandler from "@/plugins/errorHandling";
import { IStandaloneDeviceTypeOption, IListStandalone } from '@/model/Standalone/standaloneInterfaces';
import {
  PAGINATION_FIRST_PAGE,
  PAGINATION_DEFAULT_LAST_PAGE,
  PAGINATION_DEFAULT_TOTAL,
  PAGINATION_DEFAULT_PER_PAGE,
} from '@/constants/pagination';

interface StandaloneState {
  standaloneList: IListStandalone[];
  allStandaloneList: IListStandalone[];
  allCurrentPage: number;
  allLastPage: number;
  allTotal: number;
  allPerPage: number;
  hasStandalones: boolean;
  deviceTypeOptions: IStandaloneDeviceTypeOption[];
}

const state: StandaloneState = {
  standaloneList: [],
  allStandaloneList: [],
  allCurrentPage: PAGINATION_FIRST_PAGE,
  allLastPage: PAGINATION_DEFAULT_LAST_PAGE,
  allTotal: PAGINATION_DEFAULT_TOTAL,
  allPerPage: PAGINATION_DEFAULT_PER_PAGE,
  hasStandalones: true,
  deviceTypeOptions: [],
};

const getters: GetterTree<StandaloneState, RootState> = {
  standaloneList: (state): IListStandalone[] => state.standaloneList,
  allStandaloneList: (state): IListStandalone[] => state.allStandaloneList,
  allCurrentPage: (state): number => state.allCurrentPage,
  allLastPage: (state): number => state.allLastPage,
  allTotal: (state): number => state.allTotal,
  allPerPage: (state): number => state.allPerPage,
  hasStandalones: (state): boolean => state.hasStandalones !== false,
  deviceTypeOptions: (state): IStandaloneDeviceTypeOption[] => state.deviceTypeOptions,
};

const actions: ActionTree<StandaloneState, RootState> = {
  fetchDeviceTypeOptions: ({commit}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'standalones/device-types',
      method: 'GET',
    }),
    commit,
    'deviceTypeOptionsLoaded',
  ),
  fetchAllStandalones: ({commit}, filters): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'standalones/all',
      method: 'GET',
      params: filters,
    }),
    commit,
    'allStandalonesLoaded',
  ),
  clearAllStandalones: ({commit}) => commit('clearAllStandalones'),
  checkStandalonesExist: ({commit}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'standalones/all',
      method: 'GET',
      params: { per_page: 1, page: 1 },
    }),
    commit,
    'standalonesExistChecked',
  ),
  fetchStandalones: ({commit}, {lockerId, filters}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'lockers/' + lockerId + '/standalones',
      method: 'GET',
      params: filters,
    }),
    commit,
        'standalonesLoaded',
  ),
  fetchStandalonesByCommunity: ({commit}, {communityId, filters}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'communities/' + communityId + '/standalones',
      method: 'GET',
      params: filters,
    }),
    commit,
    'standalonesLoaded',
  ),
  createNewStandalone: ({ commit }, {communityId, digitalPortalType} ): Promise<boolean> => {
    const response = responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'communities/' + communityId + '/standalones/new',
        method: 'POST',
        data: { digital_portal_type: digitalPortalType },
      }),
      commit,
      'newStandaloneCreated'
    );
    return Promise.resolve(response).then(() => {
      return true;
    });
  },
  rebootStandalone: ({ commit }, {standaloneId}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'standalones/' + standaloneId + '/reboot',
      method: 'POST'
    }),
    commit,
    'rebootLocker',
    null,
    false
  ),

    refreshBrowser: ({ commit }, {standaloneId}): any => responseHandler.handle(
        () => axios({
            url: settings.endpoint + 'standalones/' + standaloneId + '/refresh',
            method: 'POST'
        }),
        commit,
        'refreshBrowser',
        null,
        false
    ),

  sshConnection: ({ commit }, {standaloneId}): any => responseHandler.handle(
    () => axios({
        url: settings.endpoint + 'standalones/' + standaloneId + '/connect',
        method: 'POST'
    }),
    commit,
    'sshConnection',
    null,
    false
  ),

  screenshot: ({ commit }, {standaloneId}): any => responseHandler.handle(
     () => axios({
         url: settings.endpoint + 'standalones/' + standaloneId + '/screenshot',
         method: 'POST'
     }),
     commit,
     'screenShot',
     null,
     false
  ),

  updateStandaloneSim: ({ commit }, {standaloneId, sim}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'standalones/' + standaloneId + '/sim',
      method: 'PUT',
      data: { sim },
    }),
    commit,
    'standaloneSimUpdated',
  ),

  updateStandaloneType: ({ commit }, {standaloneId, digitalPortalType}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'standalones/' + standaloneId + '/type',
      method: 'PUT',
      data: { digital_portal_type: digitalPortalType },
    }),
    commit,
    'standaloneTypeUpdated',
  ),

  updateStandaloneName: ({ commit }, {standaloneId, name}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'standalones/' + standaloneId + '/name',
      method: 'PUT',
      data: { name },
    }),
    commit,
    'standaloneNameUpdated',
  ),

  deleteStandalone: ({ commit }, {standaloneId}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'standalones/' + standaloneId,
      method: 'DELETE',
    }),
    commit,
    'standaloneDeleted',
  ),

  markStandaloneAsDemo: ({ commit }, {standaloneId}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'standalones/' + standaloneId + '/mark-demo',
      method: 'POST',
    }),
    commit,
    'standaloneDemoMarked',
  ),
};

const mutations: MutationTree<StandaloneState> = {
  standalonesLoaded(state, payload: any) {
    state.standaloneList = payload;
  },
  deviceTypeOptionsLoaded(state, payload: IStandaloneDeviceTypeOption[]) {
    state.deviceTypeOptions = payload || [];
  },
  allStandalonesLoaded(state, payload: any) {
    if (!payload) {
      state.allStandaloneList = [];
      return;
    }
    state.allStandaloneList = Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []);
    state.allCurrentPage = payload.current_page || PAGINATION_FIRST_PAGE;
    state.allLastPage = payload.last_page || PAGINATION_DEFAULT_LAST_PAGE;
    state.allTotal = payload.total || PAGINATION_DEFAULT_TOTAL;
    state.allPerPage = payload.per_page || PAGINATION_DEFAULT_PER_PAGE;
  },
  clearAllStandalones(state) {
    state.allStandaloneList = [];
    state.allCurrentPage = PAGINATION_FIRST_PAGE;
    state.allLastPage = PAGINATION_DEFAULT_LAST_PAGE;
    state.allTotal = PAGINATION_DEFAULT_TOTAL;
    state.allPerPage = PAGINATION_DEFAULT_PER_PAGE;
  },
  standalonesExistChecked(state, payload: any) {
    if (payload && payload.total !== undefined) {
      state.hasStandalones = payload.total > 0;
    }
    // on null/error payload keep hasStandalones = true (show by default)
  },
  newStandaloneCreated(state) {
    // from here we can update the state and avoid reloading the page
  },
  standaloneSimUpdated(state, payload: any) {
    const idx = state.standaloneList.findIndex(s => s.standalone_id === payload.standalone_id);
    if (idx !== -1) {
      state.standaloneList[idx].sim = payload.sim;
    }
  },
  standaloneTypeUpdated(state, payload: any) {
    updateStandaloneInLists(state, payload);
  },
  standaloneDemoMarked(state, payload: any) {
    updateStandaloneInLists(state, payload);
  },
  standaloneDeleted(state, payload: any) {
    if (payload && payload.standalone_id) {
      const id: number = payload.standalone_id;
      state.standaloneList = state.standaloneList.filter(function(s) { return s.standalone_id !== id; });
      state.allStandaloneList = state.allStandaloneList.filter(function(s) { return s.standalone_id !== id; });
    }
  },
};

function updateStandaloneInLists(state: StandaloneState, payload: IListStandalone) {
  const updateList = (list: IListStandalone[]) => {
    const idx = list.findIndex(s => s.standalone_id === payload.standalone_id);
    if (idx !== -1) {
      Vue.set(list, idx, {...list[idx], ...payload});
    }
  };

  updateList(state.standaloneList);
  updateList(state.allStandaloneList);
}

const namespaced: boolean = true;

export const standaloneModule: Module<StandaloneState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations
};
